import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';

/**
 * When the driver actually delivered and collected each cleaner for THIS
 * booking — appended to the "Cleaners Assigned" line rather than taking a row
 * of its own (2026-07-27). It is a fact ABOUT those cleaners, so it reads
 * as part of that sentence instead of a separate table or label.
 *
 * Renders a bare <span>: the caller owns the <p>.
 *
 * The scheduling board deliberately shows a drop-off time only while it is
 * still the live question ("at unit · not started"). This is the opposite
 * surface — the record — so it shows the full picture: dropped off at, picked
 * up at.
 *
 * Read straight from route_stops, which is what the driver's app writes, so it
 * cannot drift from what actually happened.
 */
export default function BookingDriverStops({ bookingId, bookingStart }) {
    const [stops, setStops] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!bookingId || !bookingStart) return;
        // The day is the BOOKING's Dubai wall date — never the viewer's.
        const day = DateTime.fromISO(bookingStart, { zone: 'utc' })
            .setZone('Asia/Dubai')
            .toFormat('yyyy-MM-dd');

        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/routing/${day}/stops`);
                if (cancelled) return;
                setStops((res.data.stops || []).filter((s) => s.booking_id === Number(bookingId)));
            } catch (e) {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => { cancelled = true; };
    }, [bookingId, bookingStart]);

    // Routing is additive — if it is unavailable the rest of the page is fine.
    if (failed || !stops || stops.length === 0) return null;

    const t = (iso) => (iso
        ? DateTime.fromISO(iso, { zone: 'utc' }).setZone('Asia/Dubai').toFormat('HH:mm')
        : null);

    // One row per cleaner: her delivery to this booking and her collection from
    // it. A courier booking has no cleaner, so it gets a single unnamed row.
    const byCleaner = new Map();
    stops.forEach((s) => {
        const key = s.cleaner_id ?? 'courier';
        const row = byCleaner.get(key) || {
            key,
            name: s.cleaner_id
                ? [s.cleaner_first_name, s.cleaner_last_name].filter(Boolean).join(' ')
                : (s.booking_type || 'Courier task'),
            droppedAt: null,
            collectedAt: null,
            skipped: false,
        };
        if (s.status === 'done') {
            if (s.stop_type === 'dropoff') row.droppedAt = s.completed_at;
            if (s.stop_type === 'collect') row.collectedAt = s.completed_at;
        } else if (s.status === 'skipped') {
            row.skipped = true;
        }
        byCleaner.set(key, row);
    });

    const rows = [...byCleaner.values()];
    // Nothing has happened yet — dashes are noise on a booking the driver
    // hasn't reached, and this is riding on someone else's line.
    if (!rows.some((r) => r.droppedAt || r.collectedAt)) return null;

    const part = (r) => {
        if (!r.droppedAt && !r.collectedAt) return null;
        // Compact on purpose — it shares a row. "dropped 11:03, picked up
        // 14:22" for one cleaner; prefixed with her name when there are several.
        const bits = [];
        if (r.droppedAt) bits.push(`dropped ${t(r.droppedAt)}`);
        if (r.collectedAt) bits.push(`picked up ${t(r.collectedAt)}`);
        // The single-cleaner case already has her name at the start of the line.
        return rows.length === 1 ? bits.join(', ') : `${r.name} ${bits.join(', ')}`;
    };

    const text = rows.map(part).filter(Boolean).join(' · ');
    if (!text) return null;

    return <span className="booking-driver-stops"> · {text}</span>;
}
